import { reducerWithInitialState } from 'typescript-fsa-reducers/dist';
import {
  ContentSetup,
  ProgressLoaded} from '../../utils';
import { nextProblemChosen, progressStateLoaded } from '../actions/progression';
import {
  problemLoaded,
  networkFailure,
  networkRetry,
  submitResponse,
  logEvent
} from '../actions/ui';
import { pageContentFactory } from '../actions/content';
import { PageContent } from '../../page-content/page-content';
import { RawPageData, ITSEvent } from 'its-pages';
import store from '../store';
import { createStartProblem } from '../middleware/progression';

export interface CurrentProblemState {
  rawContent?: RawPageData;
  pageContent?: PageContent;
  hasResponse: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  networkError: boolean;
  networkRetry: boolean;
  isValidResponse: boolean;
  setup?: ContentSetup;
  score: number;
  progressDenominator: number;
  progressNumerator: number;
}

const INITIAL_STATE: CurrentProblemState = {
  hasResponse: false,
  isLoaded: false,
  isLoading: false,
  networkError: false,
  networkRetry: false,
  isValidResponse: false,
  score: 0,
  progressDenominator: 1,
  progressNumerator: 0
};

const nextProblemChosenReducer = (
  state: CurrentProblemState,
  setup: ContentSetup
): CurrentProblemState => {
  return {
    ...state,
    isLoaded: false,
    isLoading: true,
    setup
  };
};

const dispatchLoggerEvent = (event: ITSEvent) => {
  store.dispatch(logEvent(event));
};

const problemLoadedReducer = (
  state: CurrentProblemState,
  rawPageData: RawPageData
): CurrentProblemState => ({
  ...state,
  rawContent: rawPageData,
  pageContent: pageContentFactory({
    rawPageData,
    itsEventLogger: dispatchLoggerEvent
  }),
  hasResponse: false,
  isLoaded: true,
  isLoading: false,
  networkError: false,
  networkRetry: false,
  isValidResponse: false,
  score: 0
});

function networkFailureReducer(
  state: CurrentProblemState
): CurrentProblemState {
  return {
    ...state,
    networkError: true
  };
}

function networkRetryReducer(state: CurrentProblemState): CurrentProblemState {
  return {
    ...state,
    networkRetry: true,
    networkError: false
  };
}

const submitResponseReducer = (
  state: CurrentProblemState
): CurrentProblemState => {
  if (state.pageContent === undefined) {
    return { ...state, isValidResponse: false };
  }

  const pageData = state.pageContent.pageData;
  const score = pageData.score;

  return {
    ...state,
    hasResponse: true,
    isValidResponse: true,
    score
  };
};

const progressStateLoadedReducer = (
  state: CurrentProblemState,
  savedState: ProgressLoaded
): CurrentProblemState => {
  const {
    moduleState: { problemSetupState },
    moduleInfo
  } = savedState;
  if (!problemSetupState) {
    return state;
  }

  const { progressionIndex, difficulty } = problemSetupState;
  const setup = createStartProblem(moduleInfo);

  setup.difficulty = difficulty;
  setup.progressionIndex = progressionIndex;

  return {
    ...state,
    setup
  };
};

// TODO: Need to reset the setup property on bug report change

export const currentProblemReducers = reducerWithInitialState(INITIAL_STATE)
  .case(nextProblemChosen, nextProblemChosenReducer)
  .case(problemLoaded, problemLoadedReducer)
  .case(networkFailure, networkFailureReducer)
  .case(networkRetry, networkRetryReducer)
  .case(progressStateLoaded, progressStateLoadedReducer)
  .case(submitResponse, submitResponseReducer);
